/* ==========================================================================
   The LAB — Rewards SDK (shared across all reward sections)
   Loads the LoyaltyLion SDK, stores customer data globally,
   and dispatches a CustomEvent for section scripts to listen to.
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Tier configuration — reads from Liquid-injected window.LAB_TIERS_CONFIG.
     Falls back to hardcoded values if not available.
     Tier names MUST match LoyaltyLion dashboard tier names exactly.
     -------------------------------------------------------------------------- */

  const LAB_TIERS = (window.LAB_TIERS_CONFIG && window.LAB_TIERS_CONFIG.length)
    ? window.LAB_TIERS_CONFIG.map((t) => ({
        name: String(t.name || ''), threshold: Number(t.threshold) || 0
      }))
    : [
        { name: 'Intern', threshold: 0 },
        { name: 'Manager', threshold: 300 },
        { name: 'Director', threshold: 500 },
        { name: 'VP', threshold: 800 },
        { name: 'Board Member', threshold: 1500 }
      ];

  function getTierByPoints(points) {
    for (let i = LAB_TIERS.length - 1; i >= 0; i--) {
      if (points >= LAB_TIERS[i].threshold) return LAB_TIERS[i];
    }
    return LAB_TIERS[0];
  }

  function getTierByName(name) {
    if (!name) return LAB_TIERS[0];
    const tier = LAB_TIERS.find((t) => t.name.toLowerCase() === name.toLowerCase());
    return tier || LAB_TIERS[0];
  }

  function getNextTier(currentTierName) {
    const idx = LAB_TIERS.findIndex((t) => t.name.toLowerCase() === (currentTierName || '').toLowerCase());
    if (idx === -1) return LAB_TIERS[1];
    if (idx >= LAB_TIERS.length - 1) return null;
    return LAB_TIERS[idx + 1];
  }

  function calculateProgress(points, currentTierName) {
    const current = getTierByName(currentTierName);
    const next = getNextTier(currentTierName);
    if (!next) return 100;
    const range = next.threshold - current.threshold;
    if (range <= 0) return 100;
    const progress = ((points - current.threshold) / range) * 100;
    return Math.max(0, Math.min(100, progress));
  }

  /* --------------------------------------------------------------------------
     Currency conversion — converts EUR amounts to local currency
     Reads from Liquid-injected window.LAB_CURRENCY_CONFIG.
     Used for benefit text that contains {amount} placeholders.
     -------------------------------------------------------------------------- */

  const CURRENCY_CONFIG = window.LAB_CURRENCY_CONFIG || {
    current: 'EUR',
    rates: { EUR: { rate: 1, symbol: '€', position: 'before' } }
  };

  function formatAmount(eurAmount) {
    const currency = CURRENCY_CONFIG.current || 'EUR';
    const config = CURRENCY_CONFIG.rates[currency] || CURRENCY_CONFIG.rates['EUR'];
    const converted = eurAmount * config.rate;
    // Only round to nearest 50 for non-base currencies (rate !== 1)
    const displayAmount = config.rate === 1
      ? Math.round(converted)
      : (Math.round(converted / 50) * 50 || Math.round(converted));
    if (config.position === 'after') {
      return displayAmount + ' ' + config.symbol;
    }
    return config.symbol + displayAmount;
  }

  /**
   * Process all {amount} placeholders in text nodes within a container.
   * Pattern: {120} → converted to local currency (e.g., "kr. 900" for DKK)
   */
  function convertBenefitAmounts(container) {
    if (!container) container = document;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    const regex = /\{(\d+)\}/g;
    let node;
    while ((node = walker.nextNode())) {
      if (regex.test(node.textContent)) {
        node.textContent = node.textContent.replace(regex, function(match, amount) {
          return formatAmount(parseInt(amount, 10));
        });
      }
      regex.lastIndex = 0;
    }
  }

  // Auto-process static benefit text on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { convertBenefitAmounts(); });
  } else {
    convertBenefitAmounts();
  }

  /* --------------------------------------------------------------------------
     Drawer utility — shared by order drawer and tier drawer
     -------------------------------------------------------------------------- */

  function initDrawer(drawerId) {
    const overlay = document.querySelector('[data-drawer-overlay="' + drawerId + '"');
    const drawer = document.querySelector('[data-drawer="' + drawerId + '"');
    if (!overlay || !drawer) return null;

    function open() {
      overlay.classList.add('is-open');
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      overlay.classList.remove('is-open');
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    const closeBtns = drawer.querySelectorAll('[data-drawer-close]');
    closeBtns.forEach((btn) => {
      btn.addEventListener('click', close);
    });

    return { open: open, close: close };
  }

  /* --------------------------------------------------------------------------
     SDK initialization — three-pronged approach
     -------------------------------------------------------------------------- */

  function onLoyaltyLionReady(callback) {
    function tryInit() {
      try {
        if (window.loyaltylion && window.loyaltylion.customer && window.loyaltylion.customer.id) {
          callback(window.loyaltylion.customer);
          return true;
        }
      } catch (e) { /* SDK not ready yet */ }
      return false;
    }

    if (tryInit()) return;

    document.addEventListener('loyaltylion.init', () => {
      tryInit();
    });

    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      if (tryInit() || attempts >= 40) {
        clearInterval(poll);
      }
    }, 250);

    setTimeout(() => {
      if (!window.loyaltylion || !window.loyaltylion.customer) {
        document.dispatchEvent(new CustomEvent('rewards:sdk-timeout'));
      }
    }, 8000);
  }

  /* --------------------------------------------------------------------------
     Global state + customer-ready event
     -------------------------------------------------------------------------- */

  window.labRewards = {
    tiers: LAB_TIERS,
    customer: null,
    getTierByPoints: getTierByPoints,
    getTierByName: getTierByName,
    getNextTier: getNextTier,
    calculateProgress: calculateProgress,
    initDrawer: initDrawer,
    onReady: onLoyaltyLionReady,
    formatAmount: formatAmount,
    convertBenefitAmounts: convertBenefitAmounts
  };

  // Initialize SDK and dispatch event for all section scripts
  onLoyaltyLionReady((customer) => {
    window.labRewards.customer = customer;
    document.dispatchEvent(new CustomEvent('rewards:customer-ready', { detail: customer }));
  });

  // SDK timeout — dispatch so sections can handle graceful degradation
  document.addEventListener('rewards:sdk-timeout', () => {
    window.labRewards.customer = null;
    // Sections listen for this event independently
  });
})();
